import { useInfiniteQuery, useMutation } from 'react-query';
import {
  getBrand,
  getModel,
  listBrandsFilter,
  listBrandsModels,
  listModelsFilter,
} from './BrandsModelsVersions.service';
import {
  Brand,
  BrandsModelsRequest,
  BrandsRequest,
  Model,
  ModelsRequest,
} from './BrandsModelsVersions.types';
import { PageRequest } from '@/core/request/PageRequest';
import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { queryClient } from '@/Root';
import notificationError from '@/utils/functions/notificationError';

export function useBrandsFilter(params: BrandsRequest, enabled?: boolean) {
  const cache = queryClient.getQueryData(['brands', { ...params }]);

  return useInfiniteQuery(
    ['brands', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listBrandsFilter(req);
    },
    {
      enabled: enabled && !cache,
      getNextPageParam: (lastPage) => lastPage.nextToken,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useBrandMutation() {
  return useMutation(
    async (brandId: number) => {
      const cache = queryClient.getQueryData<Brand>(['brand', brandId]);

      if (!!cache) return cache;

      return getBrand(brandId);
    },
    {
      onSuccess(data, brandId) {
        queryClient.setQueryData(['brand', brandId], data);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useModelsFilter(params: ModelsRequest, enabled = true) {
  const cache = queryClient.getQueryData(['models', { ...params }]);

  return useInfiniteQuery(
    ['models', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listModelsFilter(req);
    },
    {
      enabled: !!params.brandId && enabled && !cache,
      getNextPageParam: (lastPage) => lastPage.nextToken,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useModelMutation() {
  return useMutation(
    async (modelId: number) => {
      const cache = queryClient.getQueryData<Model>(['model', modelId]);

      if (!!cache) return cache;

      return getModel(modelId);
    },
    {
      onSuccess(data, modelId) {
        queryClient.setQueryData(['model', modelId], data);
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useBrandsModels(params?: BrandsModelsRequest, enabled = true) {
  const cache = queryClient.getQueryData(['brands-models', { ...params }]);

  return useInfiniteQuery(
    ['brands-models', { ...params }],
    ({ pageParam }) => {
      const req: PageRequest = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listBrandsModels(req);
    },
    {
      enabled: enabled && !cache,
      getNextPageParam: (lastPage) => lastPage.nextToken,
      onError(error) {
        notificationError(error);
      },
    },
  );
}
